import { memo, useState } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFlowBuilderStore } from '@/stores/flow-builder-store';
import { MESSAGING_CONDITION_TYPES } from '@/types/field-mapping-flow';
import type { AddBranchNodeData } from '@/types/field-mapping-flow';

function AddBranchNodeComponent({ data }: NodeProps) {
  const _nodeData = data as unknown as AddBranchNodeData;
  const addBranch = useFlowBuilderStore((s) => s.addBranch);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [conditionValue, setConditionValue] = useState('');

  const handleCreate = () => {
    if (!selectedType || !conditionValue) return;

    const condType = MESSAGING_CONDITION_TYPES.find((t) => t.value === selectedType);
    if (!condType) return;

    const filterValue = `${condType.prefix}${conditionValue}`;
    const filterLabel = `${condType.label}: ${conditionValue}`;
    addBranch(filterValue, filterLabel);

    setShowDropdown(false);
    setSelectedType('');
    setConditionValue('');
  };

  return (
    <div className="flex flex-col items-center">
      <Handle
        type="target"
        position={Position.Top}
        className="!h-2 !w-2 !border !border-border !bg-background"
      />

      {!showDropdown ? (
        <button
          onClick={() => setShowDropdown(true)}
          className="flex h-10 w-10 items-center justify-center rounded-lg bg-background text-muted-foreground transition-all hover:text-primary"
          style={{ border: '0.5px dashed hsl(var(--border) / 0.4)' }}
        >
          <Plus className="h-5 w-5" />
        </button>
      ) : (
        <div className="w-[220px] rounded-lg bg-background p-3 shadow-sm" style={{ border: '0.5px solid hsl(var(--border) / 0.4)' }}>
          <p className="mb-2 text-xs font-medium text-foreground">Add Condition Branch</p>

          {/* Condition type select */}
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="mb-2 w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground"
          >
            <option value="">Select condition type...</option>
            {MESSAGING_CONDITION_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>

          {/* Condition value */}
          {selectedType && (
            <input
              type="text"
              value={conditionValue}
              onChange={(e) => setConditionValue(e.target.value)}
              placeholder="Enter value..."
              className="mb-2 w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
            />
          )}

          <div className="flex gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 text-xs"
              onClick={() => {
                setShowDropdown(false);
                setSelectedType('');
                setConditionValue('');
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="flex-1 text-xs"
              disabled={!selectedType || !conditionValue}
              onClick={handleCreate}
            >
              Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export const AddBranchNode = memo(AddBranchNodeComponent);
